import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ComplianceCoverage,
  ContractualCompliance,
  ControlPrioritizationOption,
  CreateUpdateSecurityPlan,
  CriticalSystems,
  DatesOverlap,
  PlanStatus,
  PlanStatusDropdownOption,
  PlanStrategyOption,
  SecurityPlan,
  SecurityPlanDetails,
  SecurityPlanRiskCoverage,
  LocalLawsProtection,
  StrategyOption,
  SystemSecurityScore,
} from '../interfaces';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SecurityPlanService {
  constructor(private http: HttpClient) {}

  updateCreateSecurityPlan(
    securityPlan: CreateUpdateSecurityPlan,
  ): Observable<string> {
    return this.http.post<string>(
      `${environment.baseUrl}/CMDISOSecurityPlan/CreateUpdatePlan`,
      securityPlan,
    );
  }

  updatePlanStatus(
    planId: string,
    newStatusId: PlanStatus,
  ): Observable<SecurityPlan[]> {
    return this.http
      .put<SecurityPlan[]>(
        `${environment.baseUrl}/CMDISOSecurityPlan/UpdatePlanStatus`,
        {
          planId,
          newStatusId,
        },
      )
      .pipe(map(this.formatPlans));
  }

  fetchPlanDetails(id: string): Observable<SecurityPlanDetails> {
    return this.http.get<SecurityPlanDetails>(
      `${environment.baseUrl}/CMDISOSecurityPlan/GetDetailsForSidebar`,
      {
        params: {
          planId: id,
        },
      },
    );
  }

  fetchSecurityPlanNames(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(
      `${environment.baseUrl}/CMDISOSecurityPlan/GetPlanNames`,
    );
  }

  fetchSecurityPlans(): Observable<{
    securityPlans: SecurityPlan[];
    canManagePlans: boolean;
  }> {
    return this.http
      .get<any>(`${environment.baseUrl}/CMDISOSecurityProgram/GetPlansList`)
      .pipe(
        map((data) => ({
          securityPlans: this.formatPlans(data),
          canManagePlans: !!data.canManagePlans,
        })),
      );
  }

  formatPlans(data: any): SecurityPlan[] {
    return [
      ...data.newPlans.map((plan: any) => ({
        ...plan,
        id: plan.planId,
        name: plan.planName,
        operationStartDate: plan.startDate,
        operationEndDate: plan.endDate,
        riskCoverage: plan.riskMitigation,
        status: +plan.status,
      })),
      ...data.approvedPlans.map((plan: any) => ({
        ...plan,
        id: plan.planId,
        name: plan.planName,
        operationStartDate: plan.startDate,
        operationEndDate: plan.endDate,
        riskCoverage: plan.riskMitigation,
        status: +plan.status,
      })),
    ];
  }

  fetchSystemSecurityScore(): Observable<SystemSecurityScore> {
    return this.http.get<SystemSecurityScore>(
      `${environment.baseUrl}/CMDISOSecurityProgram/GetSystemSecurityScore`,
    );
  }

  fetchRiskCoverage(): Observable<SecurityPlanRiskCoverage> {
    return this.http.get<SecurityPlanRiskCoverage>(
      `${environment.baseUrl}/CMDISORiskInOrg/GetRiskCoverageBarData`,
    );
  }

  fetchContractualCompliance(): Observable<ContractualCompliance> {
    return this.http.get<ContractualCompliance>(
      `${environment.baseUrl}/CMDISOSecurityProgram/CalculateContractualCompliance`,
    );
  }

  fetchComplianceCoverage(): Observable<ComplianceCoverage> {
    return this.http.get<ComplianceCoverage>(
      `${environment.baseUrl}/CMDISOSecurityProgram/CalculateComplianceCoverage`,
    );
  }

  fetchCriticalSystems(): Observable<CriticalSystems> {
    return this.http
      .get<any>(
        `${environment.baseUrl}/CMDISOSecurityProgram/GetCriticalSystemsCoverage`,
      )
      .pipe(
        map((data) => {
          if (!data.systems?.length) {
            return {
              financial: {
                covered: 0,
                completed: 0,
              },
              coreBusiness: {
                covered: 0,
                completed: 0,
              },
              ot: {
                covered: 0,
                completed: 0,
              },
              infrastructure: {
                covered: 0,
                completed: 0,
              },
            };
          }
          return data;
        }),
      );
  }

  fetchLocalLawsProtection(): Observable<LocalLawsProtection> {
    return this.http.get<LocalLawsProtection>(
      `${environment.baseUrl}/CMDISOSecurityProgram/GetSensitiveDataProtectionCoverage`,
    );
  }

  fetchDatesOverlap(
    startingDate: string,
    planLength: number,
    planId?: string,
  ): Observable<DatesOverlap> {
    const params: {
      startingDate: string;
      planLength: number;
      planId?: string;
    } = {
      startingDate: startingDate.split('T')[0],
      planLength,
    };
    if (planId) {
      params.planId = planId;
    }
    return this.http.post<DatesOverlap>(
      `${environment.baseUrl}/CMDISOSecurityPlan/CheckApprovedPlanOverlap`,
      params,
    );
  }

  fetchDatesChangeRequired(
    startingDate: string,
    planLength: number,
    planId: string,
  ): Observable<{ hasImpact: boolean; messageId: string }> {
    const params: {
      newStartingDate: string;
      newPlanLength: number;
      planId: string;
    } = {
      newStartingDate: startingDate.split('T')[0],
      newPlanLength: planLength,
      planId,
    };
    return this.http.post<{ hasImpact: boolean; messageId: string }>(
      `${environment.baseUrl}/CMDISOSecurityPlan/CheckPlanDateChangeImpact`,
      params,
    );
  }

  fetchPlanStatusDropdownOptions(): Observable<PlanStatusDropdownOption[]> {
    return this.http.get<PlanStatusDropdownOption[]>(
      `${environment.baseUrl}/CMDISOSecurityPlan/GetPlanStatuses`,
    );
  }

  fetchPresetBudgetTypeOptions(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(
      `${environment.baseUrl}/CMDISOSecurityPlan/GetPlanPresetBudgetTypes`,
    );
  }

  fetchPlanBudgetTypes(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(
      `${environment.baseUrl}/CMDISOSecurityPlan/GetPlanBudgetTypes`,
    );
  }

  fetchStrategyOptions(): Observable<PlanStrategyOption<StrategyOption>[]> {
    return this.http.get<PlanStrategyOption<StrategyOption>[]>(
      `${environment.baseUrl}/CMDISOPlanStrategyParameters/StrategyOptions`,
    );
  }

  fetchControlPrioritizationOptions(): Observable<
    PlanStrategyOption<ControlPrioritizationOption>[]
  > {
    return this.http.get<PlanStrategyOption<ControlPrioritizationOption>[]>(
      `${environment.baseUrl}/CMDISOPlanStrategyParameters/ControlPrioritizationOptions`,
    );
  }

  fetchPlanConfigurationParameters(): Observable<
    PlanStrategyOption<ControlPrioritizationOption | StrategyOption>[]
  > {
    return this.http.get<
      PlanStrategyOption<ControlPrioritizationOption | StrategyOption>[]
    >(
      `${environment.baseUrl}/CMDISOPlanStrategyParameters/PlanConfigurationParameters`,
    );
  }
}
